import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Logo } from '../components/Logo';
import { useAuth } from '../hooks/useAuth';

interface SignUpFormData {
  workEmail: string;
  firstName: string;
  lastName: string;
  institution: string;
  password: string;
  confirmPassword: string;
  agreedToTerms: boolean;
}

interface SignUpFormErrors {
  workEmail?: string;
  firstName?: string;
  lastName?: string;
  institution?: string;
  password?: string;
  confirmPassword?: string;
  agreedToTerms?: string;
}

const SignUpPage: React.FC = () => {
  const navigate = useNavigate();
  const { signUp, user } = useAuth();
  const [formData, setFormData] = useState<SignUpFormData>({
    workEmail: '',
    firstName: '',
    lastName: '',
    institution: '',
    password: '',
    confirmPassword: '',
    agreedToTerms: false
  });
  const [errors, setErrors] = useState<SignUpFormErrors>({});
  const [focusedField, setFocusedField] = useState<keyof SignUpFormData | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Redirect if user is already logged in
  useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  const validateEmail = (email: string) => {
    const personalDomains = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com'];
    if (!email) return 'Email is required';
    if (!email.includes('@')) return 'Invalid email format';
    const domain = email.split('@')[1];
    if (personalDomains.includes(domain)) return 'Please use your work email';
    return '';
  };

  const validateName = (name: string, maxLength: number) => {
    if (!name) return 'This field is required';
    if (name.length > maxLength) return `Must be ${maxLength} characters or fewer`;
    if (!/^[a-zA-Z\s]*$/.test(name)) return 'Cannot contain numbers or special characters';
    return '';
  };

  const validatePassword = (password: string) => {
    if (!password) return 'Password is required';
    if (password.length < 8) return 'Must be at least 8 characters';
    if (!/[A-Z]/.test(password)) return 'Must contain at least 1 uppercase letter';
    if (!/[a-z]/.test(password)) return 'Must contain at least 1 lowercase letter';
    if (!/[0-9]/.test(password)) return 'Must contain at least 1 number';
    return '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setSuccessMessage(null);
    setIsSubmitting(true);

    // Check if there are any existing errors
    const hasErrors = Object.values(errors).some(error => error !== '');

    // Perform one final validation of all fields
    const validationErrors: SignUpFormErrors = {};
    validationErrors.workEmail = validateEmail(formData.workEmail);
    validationErrors.firstName = validateName(formData.firstName, 20);
    validationErrors.lastName = validateName(formData.lastName, 30);
    validationErrors.password = validatePassword(formData.password);
    validationErrors.confirmPassword =
      formData.password !== formData.confirmPassword ? 'Passwords do not match' : '';
    validationErrors.agreedToTerms =
      !formData.agreedToTerms ? 'You must agree to the Privacy Policy and Terms of Service' : '';

    // Update errors state with any new errors found
    const newErrors = Object.fromEntries(
      Object.entries(validationErrors).filter(([_, value]) => value !== '')
    );

    if (Object.keys(newErrors).length > 0 || hasErrors) {
      setErrors(prev => ({ ...prev, ...newErrors }));
      setIsSubmitting(false);
      return;
    }

    try {
      await signUp({
        email: formData.workEmail,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        institution: formData.institution
      });

      // Show success message
      setSuccessMessage('Account created successfully! Please check your email to verify your account.');

      // Clear form
      setFormData({
        workEmail: '',
        firstName: '',
        lastName: '',
        institution: '',
        password: '',
        confirmPassword: '',
        agreedToTerms: false
      });
    } catch (error) {
      console.error('Sign up error:', error);
      setSubmitError(
        error instanceof Error
          ? error.message
          : 'An error occurred during account creation'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    const inputValue = type === 'checkbox' ? checked : value;
    setFormData(prev => ({ ...prev, [name]: inputValue }));

    // Validate on change
    let error = '';
    switch (name) {
      case 'workEmail':
        error = validateEmail(value);
        break;
      case 'firstName':
        error = validateName(value, 20);
        break;
      case 'lastName':
        error = validateName(value, 30);
        break;
      case 'institution':
        error = value.length > 50 ? 'Must be 50 characters or fewer' : '';
        break;
      case 'password':
        error = validatePassword(value);
        break;
      case 'confirmPassword':
        error = value !== formData.password ? 'Passwords do not match' : '';
        break;
      case 'agreedToTerms':
        error = !checked ? 'You must agree to the Privacy Policy and Terms of Service' : '';
        break;
    }
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* Marketing Side */}
      <div className="hidden md:flex md:w-1/3 bg-[#F55D3E] items-center justify-center p-12">
        <div className="max-w-md text-white space-y-8">
          <h1 className="text-4xl font-bold">
            Fast, expert IRB decisions, usually within 8 business hours.
          </h1>
        </div>
      </div>
      <div className="md:w-2/3 bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex justify-center">
            <Logo className="w-auto h-12" externalUrl="https://timelyirb.com" />
          </div>
          <h2 className="mt-6 text-center text-2xl font-extrabold text-gray-700">
            Create a new account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Or{' '}
            <Link to="/signin" className="font-medium text-[#F55D3E] hover:text-[#F9A594]">
              sign in to your existing account
            </Link>
          </p>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            {submitError && (
              <div className="mb-4 p-2 bg-red-100 border border-red-400 text-red-700 rounded">
                {submitError}
              </div>
            )}

            {successMessage && (
              <div className="mb-4 p-2 bg-green-100 border border-green-400 text-green-700 rounded">
                {successMessage}
              </div>
            )}

            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="workEmail" className="block text-sm font-medium text-gray-700 mb-1">
                  Work Email
                </label>
                <input
                  type="email"
                  id="workEmail"
                  name="workEmail"
                  autoComplete="email"
                  value={formData.workEmail}
                  onChange={handleInputChange}
                  onFocus={() => setFocusedField('workEmail')}
                  onBlur={() => setFocusedField(null)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#F55D3E] focus:border-transparent ${errors.workEmail ? 'border-red-500' : ''
                    }`}
                  required
                />
                {errors.workEmail && (
                  <p className="mt-1 text-sm text-red-500">{errors.workEmail}</p>
                )}
              </div>

              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                  First Name
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  autoComplete="given-name"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  onFocus={() => setFocusedField('firstName')}
                  onBlur={() => setFocusedField(null)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#F55D3E] focus:border-transparent ${errors.firstName ? 'border-red-500' : ''
                    }`}
                  required
                />
                {errors.firstName && (
                  <p className="mt-1 text-sm text-red-500">{errors.firstName}</p>
                )}
              </div>

              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  autoComplete="family-name"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  onFocus={() => setFocusedField('lastName')}
                  onBlur={() => setFocusedField(null)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#F55D3E] focus:border-transparent ${errors.lastName ? 'border-red-500' : ''
                    }`}
                  required
                />
                {errors.lastName && (
                  <p className="mt-1 text-sm text-red-500">{errors.lastName}</p>
                )}
              </div>

              <div>
                <label htmlFor="institution" className="block text-sm font-medium text-gray-700 mb-1">
                  Institution
                </label>
                <input
                  type="text"
                  id="institution"
                  name="institution"
                  value={formData.institution}
                  onChange={handleInputChange}
                  onFocus={() => setFocusedField('institution')}
                  onBlur={() => setFocusedField(null)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#F55D3E] focus:border-transparent ${errors.institution ? 'border-red-500' : ''
                    }`}
                  required
                />
                {errors.institution && (
                  <p className="mt-1 text-sm text-red-500">{errors.institution}</p>
                )}
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  autoComplete="new-password"
                  value={formData.password}
                  onChange={handleInputChange}
                  onFocus={() => setFocusedField('password')}
                  onBlur={() => setFocusedField(null)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#F55D3E] focus:border-transparent ${errors.password ? 'border-red-500' : ''
                    }`}
                  required
                />
                {focusedField === 'password' && (
                  <p className="mt-1 text-xs text-gray-500">
                    Password must be at least 8 characters long and contain at least 1 uppercase letter,
                    1 lowercase letter, and 1 number
                  </p>
                )}
                {errors.password && (
                  <p className="mt-1 text-sm text-red-500">{errors.password}</p>
                )}
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  autoComplete="new-password"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  onFocus={() => setFocusedField('confirmPassword')}
                  onBlur={() => setFocusedField(null)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#F55D3E] focus:border-transparent ${errors.confirmPassword ? 'border-red-500' : ''
                    }`}
                  required
                />
                {errors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-500">{errors.confirmPassword}</p>
                )}
              </div>

              <div className="mb-4">
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="agreedToTerms"
                      name="agreedToTerms"
                      type="checkbox"
                      checked={formData.agreedToTerms}
                      onChange={handleInputChange}
                      className={`h-4 w-4 text-[#F55D3E] focus:ring-[#F55D3E] border-gray-300 rounded ${errors.agreedToTerms ? 'border-red-500' : ''}`}
                      required
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="agreedToTerms" className="font-medium text-gray-700">
                      I agree to the{' '}
                      <a href="https://timelyirb.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#F55D3E] hover:text-[#F9A594]">
                        Privacy Policy
                      </a>{' '}
                      and{' '}
                      <a href="https://timelyirb.com/terms" target="_blank" rel="noopener noreferrer" className="text-[#F55D3E] hover:text-[#F9A594]">
                        Terms of Service
                      </a>
                    </label>
                  </div>
                </div>
                {errors.agreedToTerms && (
                  <p className="mt-1 text-sm text-red-500">{errors.agreedToTerms}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full bg-[#F55D3E] text-white py-2 px-4 rounded-lg hover:bg-[#F9A594] transition-colors duration-200 ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
              >
                {isSubmitting ? 'Creating Account...' : 'Create Account'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUpPage;
